import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { useFeatureFlag } from '../components/FeatureFlagsContext';
import { Button } from '../components/ui/button';
import {
 BookOpen, Bot, Calculator, Video, Users,
 Settings, LogOut, User as UserIcon, ArrowUpRight,
 BarChart3, ShieldCheck, ChevronRight, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import AdDisplay from '../components/AdDisplay';
import ThemeToggle from '../components/ThemeToggle';
import SurveyReminderBanner from '../components/SurveyReminderBanner';
import UserEngagementBanner from '../components/UserEngagementBanner';
import WelcomeEngagementModal from '../components/WelcomeEngagementModal';
import ReadOnlyBanner from '../components/ReadOnlyBanner';
import AchievementsStrip from '../components/AchievementsStrip';
import SkillsRadar from '../components/SkillsRadar';
import LearningRoadmap from '../components/LearningRoadmap';
import CommunityPulse from '../components/CommunityPulse';
import ToolSelector from '../components/ToolSelector';
import AICreditsWidget from '../components/AICreditsWidget';
import PaidCreditsCard from '../components/PaidCreditsCard';
import ActivityFeed from '../components/ActivityFeed';
import BrandMark from '../components/BrandMark';
import NotificationBell from '../components/NotificationBell';
import HelpHint from '../components/HelpHint';
import DashboardTour from '../components/DashboardTour';
import WelcomeVideoPopup from '../components/WelcomeVideoPopup';
import DirectMessagesWidget from '../components/DirectMessagesWidget';
import ContinueLearningCard from '../components/ContinueLearningCard';
import GettingStartedChecklist from '../components/GettingStartedChecklist';
import ConfidenceCheckin from '../components/ConfidenceCheckin';
import MemberFooter from '../components/MemberFooter';
import BetaOptInCard from '../components/BetaOptInCard';
import FreedrIQScoreCard from '../components/freedriq/FreedrIQScoreCard';
import ChallengeBanner from '../components/freedriq/ChallengeBanner';
import GrowthNudge from '../components/freedriq/GrowthNudge';
import StudyPlanCard from '../components/freedriq/StudyPlanCard';
import AccessExpiryBanner from '../components/access/AccessExpiryBanner';
import AdSlot from '../components/AdSlot';
import axios from 'axios';

const Dashboard = () => {
 const { user, logout, API } = useContext(AuthContext);
 const navigate = useNavigate();
 const storeEnabled = useFeatureFlag('store_v1');
 const [checkingSurvey, setCheckingSurvey] = useState(true);
 const [showSurveyBanner, setShowSurveyBanner] = useState(false);
 const [surveyCompleted, setSurveyCompleted] = useState(false);

 useEffect(() => {
 const checkSurveyCompletion = async () => {
   // FreedrIQ is a HARD gate for new members — admins bypass, everyone
   // else must complete the assessment on first login. The local-storage
   // "skipped" flag no longer bypasses the redirect (it caused the
   // pre-launch bug where a returning user without responses landed on
   // Dashboard without ever being prompted).
   try {
     const [freedriq, legacy] = await Promise.all([
       axios.get(`${API}/freedriq/my-latest?tier=free&phase=pre`).catch(() => ({ data: { responses: [] } })),
       axios.get(`${API}/user-survey`).catch((e) => ({ error: e })),
     ]);
     const hasFreedriq = (freedriq.data?.responses || []).length > 0;
     const hasLegacy   = !legacy.error && legacy.data && legacy.data.completed_at;
     if (hasFreedriq || hasLegacy) {
       setSurveyCompleted(true);
       setCheckingSurvey(false);
       return;
     }
     setSurveyCompleted(false);
     // Admins never get gated — they need the dashboard for ops.
     if (user?.role === 'admin') {
       setShowSurveyBanner(true);
       setCheckingSurvey(false);
       return;
     }
     navigate('/freedriq?tier=free&phase=pre&from=onboarding');
   } catch (_e) {
     setCheckingSurvey(false);
   }
 };
 if (user) checkSurveyCompletion();
 }, [user, API, navigate]);

 const handleLogout = () => {
 logout();
 navigate('/');
 toast.success('Logged out successfully');
 };

 // Discreet admin backdoor — global keyboard shortcut (Ctrl/⌘ + Shift + A)
 // routes admins straight into the Admin Portal from any page on the
 // dashboard. Non-admins never see the toast tip and the hotkey silently
 // no-ops for them so the shortcut stays invisible.
 useEffect(() => {
   if (user?.role !== 'admin') return undefined;
   const onKey = (e) => {
     const key = (e.key || '').toLowerCase();
     if (key === 'a' && e.shiftKey && (e.ctrlKey || e.metaKey)) {
       e.preventDefault();
       navigate('/admin/portal');
     }
   };
   document.addEventListener('keydown', onKey);
   // First-time tip for admins so the shortcut is discoverable.
   const seen = localStorage.getItem('fda_admin_hotkey_tip_seen');
   if (!seen) {
     const timer = setTimeout(() => {
       toast('Admin tip', {
         description: 'Press Ctrl+Shift+A anywhere to jump into the Admin Portal.',
         duration: 8000,
       });
       localStorage.setItem('fda_admin_hotkey_tip_seen', '1');
     }, 1500);
     return () => { clearTimeout(timer); document.removeEventListener('keydown', onKey); };
   }
   return () => document.removeEventListener('keydown', onKey);
 }, [user?.role, navigate]);

 if (checkingSurvey) {
 return (
 <div className="min-h-screen bg-background flex items-center justify-center" data-testid="dashboard-loading">
 <div className="flex flex-col items-center gap-4">
 <div className="h-10 w-10 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
 <p className="text-muted-foreground">Loading your workbench…</p>
 </div>
 </div>
 );
 }

 const modules = [
 { icon: BookOpen, title: 'Learning & Courses', desc: 'Structured lessons for real operators', path: '/learning',
   details: 'Self-paced video + text lessons organized by tier. Bookmark courses to get notified when new lessons drop.',
   bullets: ['Free lessons available to everyone', 'Pro unlocks the full curriculum', 'Track progress + earn badges'] },
 { icon: Bot, title: 'AI Assistant', desc: 'Grounded answers on cycles and moisture', path: '/ai-chat',
   details: 'Chat with an assistant trained on freeze-drying best practices, cycle science and troubleshooting playbooks.',
   bullets: ['Ask about drying times, temps, batch problems', 'Answers cite the academy knowledge base', 'Pro tier gets faster + longer answers'] },
 { icon: Calculator, title: 'Business Tools', desc: 'Margin, capacity, sensitivity calculators', path: '/calculators',
   details: 'Full financial + operational modeling for freeze-dry businesses — margin, capacity, sensitivity, market analysis.',
   bullets: ['Business Calculator for pricing + ROI', 'Household Calculator for hobby batches', 'Pro forma outputs you can export'] },
 { icon: Video, title: 'Video Library', desc: 'Curated tutorials & deep dives', path: '/videos',
   details: 'Every vetted freeze-drying video we could find, categorized and searchable. No YouTube rabbit-hole required.',
   bullets: ['Getting started, cycle tuning, business ops', 'Add to favourites for later', 'Fresh videos added weekly'] },
 { icon: Users, title: 'Community', desc: 'Talk shop with fellow operators', path: '/forum',
   details: 'Threaded forum for hobbyists, side-hustlers and full-scale operators. Ask, share and get notified on replies.',
   bullets: ['Categories for gear, recipes, business', 'Notifications on replies to your threads', 'Moderated by the academy team'] },
];

 const firstName = (user?.first_name || user?.email?.split('@')[0] || 'friend');
 const greet = (() => {
 const h = new Date().getHours();
 if (h < 5) return 'Still up';
 if (h < 12) return 'Good morning';
 if (h < 18) return 'Good afternoon';
 return 'Good evening';
 })();

 return (
 <div className="min-h-screen bg-background" data-testid="dashboard-page">
 {/* Quick tool selector — unified top nav */}
 <ToolSelector />

 {/* Family A — Command Dashboard: full-bleed navy hero, overlapping profile card, right-column stack */}
 <section className="relative">
 {/* Atmospheric hero band */}
 <div className="bg-navy-spotlight text-background pt-10 sm:pt-14 pb-32 sm:pb-40 relative overflow-hidden">
 <div className="container mx-auto px-6 relative z-10">
 <span className="pill-badge"><Sparkles className="w-3.5 h-3.5" /> Your workbench</span>
 <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-5 text-balance max-w-3xl" data-testid="dashboard-welcome">
 {greet}, <span className="text-amber-brand">{firstName}.</span>
 </h1>
 <p className="mt-3 text-background/70 max-w-xl">
 Pick up where you left off, or open a new workstation.
 </p>
 </div>
 {/* Subtle amber glow */}
 <div className="absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-primary/20 blur-3xl pointer-events-none" />
 <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
 </div>

 {/* Overlapping content */}
 <div className="container mx-auto px-6 -mt-24 sm:-mt-28 relative z-20">
 <ReadOnlyBanner user={user} />
 {showSurveyBanner && !surveyCompleted && (
 <div className="mb-6">
 <SurveyReminderBanner onDismiss={() => setShowSurveyBanner(false)} />
 <UserEngagementBanner />
 <WelcomeEngagementModal />
 </div>
 )}

 <div className="grid gap-6 lg:grid-cols-12">
 {/* Left — overlapping profile card */}
 <aside className="lg:col-span-4 flex flex-col gap-6" data-testid="dashboard-profile-column">
 <div className="pro-card p-6 sm:p-7 bg-card border border-border rounded-3xl shadow-lg" data-testid="dashboard-profile-card">
 <div className="flex items-center gap-4">
 <div className="h-16 w-16 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center font-heading text-2xl font-extrabold flex-shrink-0">
 {(user?.first_name?.[0] || user?.email?.[0] || '?').toUpperCase()}
 </div>
 <div className="min-w-0">
 <div className="font-heading text-lg font-extrabold truncate">
 {user?.first_name || firstName}
 </div>
 <div className="stat-label mt-0.5 flex items-center gap-1.5">
 <span>{(user?.role || 'free')} member</span>
 {user?.role === 'admin' && (
 <button
 type="button"
 onClick={() => navigate('/admin/portal')}
 title="Admin Portal (Ctrl+Shift+A)"
 aria-label="Admin Portal"
 className="inline-flex h-5 w-5 items-center justify-center rounded-md text-muted-foreground/60 hover:text-primary hover:bg-primary/10 transition-colors"
 data-testid="dashboard-admin-shield"
 >
 <ShieldCheck className="w-3.5 h-3.5" />
 </button>
 )}
 {(user?.role === 'moderator' || user?.role === 'admin') && (
 <button
 type="button"
 onClick={() => navigate('/moderator')}
 title="Moderator Portal"
 aria-label="Moderator Portal"
 className="inline-flex h-5 items-center gap-1 px-1.5 rounded-md text-[10px] font-bold uppercase tracking-wide bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200 hover:bg-emerald-200 dark:hover:bg-emerald-900/60 transition-colors"
 data-testid="dashboard-moderator-badge"
 >
 <ShieldCheck className="w-3 h-3" />
 Mod
 </button>
 )}
 {(user?.role === 'influencer' || user?.role === 'admin') && (
 <button
 type="button"
 onClick={() => navigate('/influencer')}
 title="Creator Program dashboard"
 aria-label="Creator Program"
 className="inline-flex h-5 items-center gap-1 px-1.5 rounded-md text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200 hover:bg-amber-200 dark:hover:bg-amber-900/60 transition-colors"
 data-testid="dashboard-influencer-badge"
 >
 <Sparkles className="w-3 h-3" />
 Creator
 </button>
 )}
 </div>
 </div>
 </div>
 <div className="grid grid-cols-3 gap-2 mt-6">
 <div className="text-center rounded-xl bg-primary/5 border border-primary/10 py-3">
 <div className="stat-label">Tier</div>
 <div className="mt-1 font-heading font-extrabold text-primary text-sm">{(user?.role || 'free').toUpperCase()}</div>
 </div>
 <div className="text-center rounded-xl bg-primary/5 border border-primary/10 py-3">
 <div className="stat-label">Days</div>
 <div className="mt-1 font-heading font-extrabold text-sm">
 {(() => {
 const c = user?.created_at;
 if (!c) return '—';
 return Math.max(0, Math.floor((Date.now() - new Date(c).getTime()) / 86400000));
 })()}
 </div>
 </div>
 <div className="text-center rounded-xl bg-primary/5 border border-primary/10 py-3">
 <div className="stat-label">Tools</div>
 <div className="mt-1 font-heading font-extrabold text-sm">{modules.length}</div>
 </div>
 </div>
 <Button
 onClick={() => navigate('/portal')}
 className="w-full mt-6 rounded-full font-semibold bg-primary text-primary-foreground hover:bg-primary/90"
 data-testid="profile-card-portal-btn"
 >
 Open my portal <ArrowUpRight className="ml-1.5 w-3.5 h-3.5" />
 </Button>
 {user?.role === 'free' && (
 <button
 onClick={() => navigate('/pricing')}
 className="w-full mt-3 text-xs text-primary font-semibold hover:underline text-left"
 data-testid="profile-card-upgrade-link"
 >
 Or upgrade to Pro <ArrowUpRight className="inline w-3 h-3" />
 </button>
 )}
 </div>

 {/* Consulting card (secondary CTA under profile) */}
 <div className="pro-card p-6 bg-card border border-border rounded-2xl" data-testid="consulting-shortcut">
 <span className="eyebrow">Need help</span>
 <h3 className="font-heading text-base font-bold mt-2 tracking-tight">Talk to a consultant</h3>
 <p className="text-xs text-muted-foreground mt-1 mb-4">Personalized guidance — recipes, business plans, scaling.</p>
 <Button onClick={() => navigate('/consulting')} variant="outline" size="sm" className="rounded-full font-semibold w-full" data-testid="consulting-btn">
 Request consulting <ArrowUpRight className="ml-1.5 w-3 h-3" />
 </Button>
 </div>

 <DirectMessagesWidget compact />
 </aside>

 {/* Right — stacked feed: achievements → ad → activity feed */}
 <div className="lg:col-span-8 flex flex-col gap-6" data-testid="dashboard-feed-column">
 <GettingStartedChecklist />
 <ContinueLearningCard />
 <ConfidenceCheckin />
 <AccessExpiryBanner />
 <GrowthNudge />
 <FreedrIQScoreCard />
 <AICreditsWidget />
 <PaidCreditsCard />
 <StudyPlanCard compact />
 <ChallengeBanner />
 <AdSlot location="dashboard_sidebar" variant="sidebar" />
 <AchievementsStrip />
 <LearningRoadmap />
 <CommunityPulse />
 <SkillsRadar />
 <AdDisplay location="dashboard_banner" />
 <ActivityFeed allowCommunityToggle />
 </div>
 </div>
 </div>
 </section>

 <main className="container mx-auto px-6 py-10 sm:py-14">
 {/* Workstations */}
 <div className="mb-12">
 <div className="flex items-end justify-between mb-6">
 <div>
 <span className="eyebrow">Workstations</span>
 <h2 className="font-heading text-2xl font-bold tracking-tight mt-2">Pick a tool</h2>
 </div>
 <span className="text-xs text-muted-foreground">{modules.length} modules</span>
 </div>
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 stagger">
 {modules.map((m, i) => {
 const Icon = m.icon;
 return (
 <button
 key={m.title}
 onClick={() => navigate(m.path)}
 className="pro-card-interactive text-left p-6 group animate-fade-up relative"
 style={{ '--i': i }}
 data-testid={`module-card-${i}`}
 >
 <div className="flex items-start justify-between">
 <div className="h-11 w-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
 <Icon className="w-5 h-5" strokeWidth={2} />
 </div>
 <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition" />
 </div>
 <h3 className="mt-5 font-heading text-xl font-bold tracking-tight">{m.title}</h3>
 <p className="mt-1.5 text-muted-foreground text-[14px] leading-relaxed">{m.desc}</p>
 <div className="absolute top-3 right-3" onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}>
 <HelpHint
 title={m.title}
 bullets={m.bullets}
 cta={`Open ${m.title}`}
 onCtaClick={() => navigate(m.path)}
 side="left"
 align="start"
 testId={`module-help-${i}`}
 >
 {m.details}
 </HelpHint>
 </div>
 </button>
 );
 })}
 </div>
 </div>

 {/* Quick links */}
 <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
 {storeEnabled && (
 <div className="pro-card p-6">
 <span className="eyebrow">Store</span>
 <h3 className="font-heading text-lg font-bold mt-3 tracking-tight">Curated marketplace</h3>
 <p className="text-sm text-muted-foreground mt-1.5 mb-5">Trays, bags, absorbers and machines we actually trust.</p>
 <Button onClick={() => navigate('/store')} variant="outline" className="rounded-full font-semibold" data-testid="store-shortcut-btn">
 Open store <ArrowUpRight className="ml-1.5 w-3.5 h-3.5" />
 </Button>
 </div>
 )}
 {user?.role === 'admin' ? (
 <div className="pro-card p-6">
 <span className="eyebrow flex items-center gap-1.5"><ShieldCheck className="w-3 h-3" /> Admin</span>
 <h3 className="font-heading text-lg font-bold mt-3 tracking-tight">User moderation</h3>
 <p className="text-sm text-muted-foreground mt-1.5 mb-5">Suspend, ban or restore community members.</p>
 <Button onClick={() => navigate('/admin/users')} variant="outline" className="rounded-full font-semibold" data-testid="admin-user-mgmt-btn">
 Open user management <ArrowUpRight className="ml-1.5 w-3.5 h-3.5" />
 </Button>
 <button
   onClick={() => navigate('/admin/freedriq-challenge')}
   className="mt-3 text-xs text-primary font-semibold hover:underline w-full text-left inline-flex items-center gap-1"
   data-testid="admin-challenge-link"
 >
   Manage Community Challenge <ArrowUpRight className="w-3 h-3" />
 </button>
 <button
   onClick={() => navigate('/admin/reports')}
   className="mt-2 text-xs text-primary font-semibold hover:underline w-full text-left inline-flex items-center gap-1"
   data-testid="admin-reports-link"
 >
   Community reports <ArrowUpRight className="w-3 h-3" />
 </button>
 <button
   onClick={() => navigate('/admin/bug-reports')}
   className="mt-2 text-xs text-primary font-semibold hover:underline w-full text-left inline-flex items-center gap-1"
   data-testid="admin-bug-reports-link"
 >
   Bug reports <ArrowUpRight className="w-3 h-3" />
 </button>
 <button
   onClick={() => navigate('/admin/codes')}
   className="mt-2 text-xs text-primary font-semibold hover:underline w-full text-left inline-flex items-center gap-1"
   data-testid="admin-codes-link"
 >
   Access codes (HomeLyph / D-Freeze) <ArrowUpRight className="w-3 h-3" />
 </button>
 </div>
 ) : (
 <div className="hidden lg:block">
 {/* Sidebar ad removed — single banner in main feed */}
 </div>
 )}
 </div>

 <div className="mt-8 max-w-2xl">
 <BetaOptInCard />
 </div>
 </main>
 <DashboardTour />
 {/* First-login onboarding welcome from the FDA team — once per user, ever.
     Placeholder video is /welcome.mp4 until we ship a custom team recording. */}
 {user?.id && (
 <WelcomeVideoPopup
 storageKey={`fda_dashboard_welcome_first_login_${user.id}`}
 src="/welcome.mp4"
 durationMs={7000}
 onceEver
 />
 )}
 <MemberFooter />
 </div>
 );
};

export default Dashboard;
